'use client'

import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { faFileLines } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { useDebounceValue } from 'usehooks-ts'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  useAnalyticsContext,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'
import type { CreateWatchedListingDto } from '@b-stock/saved-search-api-client'

import WatchlistErrorToastContent from '@/components/listings/watchlist/WatchlistErrorToastContent'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import {
  useClearListingNote,
  useListingNote,
  usePutListingNote,
} from '@/queries/listingNotesQuery'
import {
  allWatchedListingsQuery,
  usePutWatchedListing,
  watchedStatusQuery,
} from '@/queries/watchlistQuery'

import {
  NOTES_AMBER_BG,
  NOTES_AMBER_BORDER,
  NOTES_AMBER_TEXT_DARK,
  NOTES_INPUT_BORDER,
} from './notes-colors'

const MAX_NOTE_LENGTH = 500
const MIN_EDIT_CHAR_DELTA = 10
const NOTE_PREVIEW_LENGTH = 150

const WidgetContainer = styled.div`
  border: ${pxToRem(1)} solid ${Colors.Semantic.SpecificFunctions.Rating.rating};
  border-radius: 0.5rem;
  overflow: hidden;
  background: ${Colors.Semantic.Neutral.white};
  width: 100%;
`

const WidgetHeader = styled.div`
  background: ${NOTES_AMBER_BG};
  border-bottom: 1px solid ${NOTES_AMBER_BORDER};
  padding: 0.625rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HeaderIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.black};
  font-size: 0.875rem;
  transform: scaleX(1.333);
  flex-shrink: 0;
`

const HeaderTitle = styled.span`
  ${Typography.Overline1}
  color: ${Colors.Semantic.Neutral.black};
  flex: 1;
`

const PrivateBadge = styled.span`
  ${Typography.Subtitle3}
  background: ${NOTES_AMBER_BORDER};
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const WidgetBody = styled.div`
  min-height: 7rem;
  padding: 0.25rem 0.75rem;
`

const NoteText = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.black};
  margin: 0.5rem 0;
  white-space: pre-wrap;
  word-break: break-word;
`

const PlaceholderText = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.BStock.disabled};
  font-style: italic;
  margin: 0.5rem 0;
`

const ShowMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${NOTES_AMBER_TEXT_DARK};
  cursor: pointer;
  ${Typography.Body3_Semibold}
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 5rem;
  resize: vertical;
  border: ${pxToRem(1)} solid ${NOTES_INPUT_BORDER};
  border-radius: 0.25rem;
  padding: 0.5rem;
  ${Typography.Body2}
  font-family: inherit;
  color: ${Colors.Semantic.Neutral.black};
  margin: 0.5rem 0;
  box-sizing: border-box;

  &:focus {
    outline: ${pxToRem(2)} solid
      ${Colors.Semantic.SpecificFunctions.Rating.rating};
    outline-offset: -${pxToRem(2)};
    border-color: ${Colors.Semantic.SpecificFunctions.Rating.rating};
  }
`

const CharacterCount = styled.span<{ $overLimit: boolean }>`
  ${Typography.Caption}
  font-size: 0.75rem;
  color: ${({ $overLimit }) =>
    $overLimit
      ? Colors.Semantic.Error.highlight
      : Colors.Semantic.BStock.disabled};
  display: block;
  text-align: right;
  margin-bottom: 0.25rem;
`

const AutoSaveIndicator = styled.span<{ $success?: boolean }>`
  ${Typography.Caption}
  font-size: 0.75rem;
  color: ${({ $success }) =>
    $success
      ? Colors.Semantic.Neutral.dark_grey
      : Colors.Semantic.BStock.disabled};
  display: block;
  text-align: right;
  margin-bottom: 0.25rem;
  font-style: italic;
`

const EditActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0;
  flex-wrap: wrap;
`

const ActionButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger'
}>`
  border: none;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  cursor: pointer;
  ${Typography.Body3_Semibold}
  font-size: 0.875rem;
  transition: opacity 0.15s ease;

  ${({ $variant }) => {
    switch ($variant) {
      case 'primary':
        return `
          background: ${Colors.Semantic.SpecificFunctions.Rating.rating};
          color: ${Colors.Semantic.Neutral.black};
          &:hover:not(:disabled) { opacity: 0.85; }
          &:disabled { opacity: 0.5; cursor: not-allowed; }
        `
      case 'danger':
        return `
          background: none;
          color: ${Colors.Semantic.Error.highlight};
          padding: 0.375rem 0;
          margin-left: auto;
          &:hover:not(:disabled) { opacity: 0.7; }
          &:disabled { opacity: 0.5; cursor: not-allowed; }
        `
      default:
        return `
          background: ${Colors.Semantic.Neutral.light_grey};
          color: ${Colors.Semantic.Neutral.very_dark_grey};
          &:hover:not(:disabled) { background: ${Colors.Semantic.Neutral.medium_grey}; }
          &:disabled { opacity: 0.5; cursor: not-allowed; }
        `
    }
  }}
`

type NotesWidgetBaseProps = {
  listingId: string
  note: string | undefined
  defaultMode?: 'view' | 'edit'
  onEditComplete?: () => void
  previewLength?: number
  onExpand?: () => void
}

type NotesWidgetEditableProps = NotesWidgetBaseProps & {
  readOnly?: false
  personId: string
  accountId: string
  dateExpires: string
  /**
   * Whether the listing is on the Watchlist, from the caller's watched-status
   * read. Undefined while that read is in flight: the widget then writes
   * nothing, so a new note never watches on a guess.
   */
  wasWatchlisted: boolean | undefined
  /**
   * True when the caller's watched-status read failed, is retrying or cannot
   * run. Edits and Clear then work as usual, and a new note saves without
   * watching, since the widget cannot know the listing is un-watched.
   */
  isWatchedStateError?: boolean
  /** The listing's end time (ISO), sent with the watch a new note adds. */
  listingEndTime?: string
  /**
   * False where a new note must not watch the listing, e.g. a closed My Bids
   * card. Defaults to true.
   */
  watchOnNewNote?: boolean
  onAutoWatchlistAdd?: () => void
  /**
   * Skip the listing-notes read and show only the caller's `note`, e.g. the
   * LDP for an un-watched listing while NOTES_OUTLIVE_WATCHLIST is off.
   */
  hidesStoredNote?: boolean
}

type NotesWidgetReadOnlyProps = NotesWidgetBaseProps & {
  readOnly: true
  personId?: never
  accountId?: never
  dateExpires?: never
  wasWatchlisted?: never
  isWatchedStateError?: never
  listingEndTime?: never
  watchOnNewNote?: never
  onAutoWatchlistAdd?: never
  hidesStoredNote?: never
}

type NotesWidgetProps = NotesWidgetEditableProps | NotesWidgetReadOnlyProps

type DisplayedNote = {
  note: string | undefined
  hasReadNote: boolean
  isReadSettled: boolean
}

/**
 * The note the widget shows. An editable widget reads it from the
 * listing-notes API and shows the caller's `note` until that read succeeds;
 * a read-only widget, or one told to skip the read, makes no request and
 * shows `note`.
 * `hasReadNote` is true once `note` comes from the read; `isReadSettled` is
 * false only while its first attempt is in flight. A read that cannot run (no
 * person), is retrying or has failed counts as settled: the widget then works
 * from the caller's `note`, as it does for a failed read. With no person the
 * widget writes nothing: Save and Remove Note stay disabled and auto-save
 * skips, until the person arrives and the read runs.
 */
const useDisplayedNote = (
  personId: string,
  listingId: string,
  skipsRead: boolean,
  noteFromProps: string | undefined
): DisplayedNote => {
  const listingNote = useListingNote(
    skipsRead ? undefined : personId,
    listingId
  )
  const isFirstReadInFlight =
    listingNote.isPending &&
    listingNote.fetchStatus === 'fetching' &&
    listingNote.failureCount === 0
  const isReadSettled = skipsRead || !isFirstReadInFlight
  if (skipsRead || !listingNote.isSuccess) {
    return { note: noteFromProps, hasReadNote: false, isReadSettled }
  }
  return {
    note: listingNote.data?.note ?? undefined,
    hasReadNote: true,
    isReadSettled,
  }
}

type LastSave = { over: string | undefined; saved: string }

/**
 * The note the widget shows, and that Close returns the editor to: what this
 * widget last saved (the text, or nothing after a clear) while `note` still
 * holds what it held at that save, else `note`. A `note` from props, or from a
 * read that has not refetched yet, can lag behind a save or a clear.
 */
const useLastSavedNote = (note: string | undefined) => {
  const [lastSave, setLastSave] = useState<LastSave | null>(null)
  const noteRef = useRef(note)
  noteRef.current = note // oxlint-disable-line react/refs
  const markSaved = useCallback(
    (saved: string) => setLastSave({ over: noteRef.current, saved }),
    []
  )
  useEffect(() => {
    setLastSave((prev) => (prev && prev.over !== note ? null : prev)) // oxlint-disable-line react/set-state-in-effect
  }, [note])
  const savedNote =
    lastSave && lastSave.over === note ? lastSave.saved || undefined : note
  return { savedNote, markSaved }
}

type NoteSeedTargets = {
  setDraftNote: (draft: string) => void
  setHasSavedNote: (hasSavedNote: boolean) => void
  setMode: (mode: 'view' | 'edit') => void
  lastAutoSavedRef: RefObject<string>
  lastEditEventRef: RefObject<string>
  hasActiveNoteRef: RefObject<boolean>
  currentDraftRef: RefObject<string>
  typedRef: RefObject<boolean>
  openedRef: RefObject<boolean>
}

/**
 * Re-seeds the edit state from the listing-notes read the first time it
 * succeeds, since that state was first seeded from the caller's `note`.
 * What the server holds (saved-note flag, auto-save and analytics baselines)
 * is always re-seeded. The draft takes the read note unless the buyer has
 * typed. An editor the widget opened by itself (the caller had no note, so
 * `defaultMode` was edit) switches to view when the read finds a note, as the
 * callers do for a note they know about; one the buyer opened stays open.
 * `currentDraftRef` moves with the draft so an auto-save in the same commit
 * sees the old draft as stale and skips it.
 */
const useSeedFromListingNote = (
  note: string | undefined,
  hasReadNote: boolean,
  targets: NoteSeedTargets
) => {
  const seededRef = useRef(false)
  const targetsRef = useRef(targets)
  targetsRef.current = targets // oxlint-disable-line react/refs

  useEffect(() => {
    if (!hasReadNote || seededRef.current) return
    seededRef.current = true
    const seed = note ?? ''
    const t = targetsRef.current
    t.lastAutoSavedRef.current = seed
    t.lastEditEventRef.current = seed
    t.hasActiveNoteRef.current = !!seed
    t.setHasSavedNote(!!seed)
    if (t.typedRef.current) return
    t.currentDraftRef.current = seed
    t.setDraftNote(seed)
    if (seed && !t.openedRef.current) t.setMode('view')
  }, [hasReadNote, note])
}

type SaveNoteParams = {
  personId: string
  listingId: string
  accountId: string
  dateExpires: string
  listingEndTime: string | undefined
  note: string
  watchesListing: boolean
}

/**
 * What a save did: `saved` wrote the note only, `watched` also added the
 * listing to the Watchlist, `watch-failed` kept the note but the watch failed.
 */
type SaveOutcome = 'saved' | 'watched' | 'watch-failed'

/**
 * The note writes the widget makes. `saveNote` saves non-empty text through
 * the listing-notes API and, only when told to (a new note on an un-watched
 * listing), then PUTs the watched listing with the listing's end time, like
 * every other add path. A failed note save rejects and watches nothing; a
 * failed watch resolves `watch-failed` and keeps the note. Callers never pass
 * empty text: an empty save is a clear. `clearNote` clears the note only.
 * `isBusy` is also true while a read the save depends on is in flight, so
 * nothing writes on a guess.
 */
const useNoteWrites = (isReadSettled: boolean) => {
  const isClosingAlertEnabled = useFlag(FeatureFlags.WATCHLIST_CLOSING_ALERT)
  const putWatchedListing = usePutWatchedListing()
  const putListingNote = usePutListingNote()
  const clearListingNote = useClearListingNote()
  const { mutateAsync: putWatched } = putWatchedListing
  const { mutateAsync: putNote } = putListingNote

  const saveNote = useCallback(
    async ({
      personId,
      listingId,
      accountId,
      dateExpires,
      listingEndTime,
      note,
      watchesListing,
    }: SaveNoteParams): Promise<SaveOutcome> => {
      await putNote({ personId, listingId, accountId, note })
      if (!watchesListing) return 'saved'
      const createWatchedListingDto: CreateWatchedListingDto = {
        accountId,
        dateExpires,
        ...(isClosingAlertEnabled && listingEndTime ? { listingEndTime } : {}),
      }
      try {
        await putWatched({ personId, listingId, createWatchedListingDto })
      } catch (error) {
        console.error('Error adding listing to watchlist on note save', error)
        return 'watch-failed'
      }
      return 'watched'
    },
    [putNote, putWatched, isClosingAlertEnabled]
  )

  const isBusy =
    !isReadSettled ||
    putWatchedListing.isPending ||
    putListingNote.isPending ||
    clearListingNote.isPending

  return { saveNote, clearNote: clearListingNote.mutateAsync, isBusy }
}

/**
 * The toast a successful note save shows: the combined toast when it watched
 * the listing, today's Watchlist add-error toast when only the watch failed,
 * else the note-saved toast (`quiet` skips that one, as auto-save does).
 */
const showNoteSaveToast = (
  outcome: SaveOutcome,
  intl: IntlShape,
  quiet = false
) => {
  if (outcome === 'watch-failed') {
    showSnackbar({
      content: <WatchlistErrorToastContent isRemoving={false} />,
      options: {
        type: 'error',
        group: 'watchlist',
        autoClose: true,
        closeButton: true,
      },
    })
    return
  }
  if (outcome === 'saved' && quiet) return
  showSnackbar({
    content: intl.formatMessage({
      id:
        outcome === 'watched'
          ? 'Listing.notes.successToastWithWatchlist'
          : 'Listing.notes.successToast',
    }),
    options: { type: 'success', autoClose: true, closeButton: true },
  })
}

type TrackNoteSaveParams = {
  trackButtonClicked: ReturnType<typeof useAnalytics>['trackButtonClicked']
  screenName: string | undefined
  note: string
  isNewNote: boolean
  outcome: SaveOutcome
  lastEditEventRef: RefObject<string>
  minEditDelta: number
}

/**
 * Sends the note-save event for text not yet tracked: `save_listing_note`
 * for a new note, with `auto_added_to_watchlist` true only when the save
 * watched the listing, else `edit_listing_note` once the text moved by at
 * least `minEditDelta` characters.
 */
const trackNoteSave = ({
  trackButtonClicked,
  screenName,
  note,
  isNewNote,
  outcome,
  lastEditEventRef,
  minEditDelta,
}: TrackNoteSaveParams) => {
  if (note === lastEditEventRef.current) return
  if (isNewNote) {
    trackButtonClicked({
      buttonName: 'save_listing_note',
      screenName,
      character_count: note.length,
      auto_added_to_watchlist: outcome === 'watched',
      note_value: note,
    })
  } else {
    const charDelta = Math.abs(note.length - lastEditEventRef.current.length)
    if (charDelta < minEditDelta) return
    trackButtonClicked({
      buttonName: 'edit_listing_note',
      screenName,
      character_count: note.length,
      note_value: note,
    })
  }
  lastEditEventRef.current = note
}

const NotesWidget = ({
  listingId,
  personId = '',
  accountId = '',
  dateExpires = '',
  note: noteFromProps,
  defaultMode = 'view',
  readOnly = false,
  onEditComplete,
  previewLength = NOTE_PREVIEW_LENGTH,
  onExpand,
  wasWatchlisted,
  isWatchedStateError = false,
  listingEndTime,
  watchOnNewNote = true,
  onAutoWatchlistAdd,
  hidesStoredNote = false,
}: NotesWidgetProps) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const {
    note: displayedNote,
    hasReadNote,
    isReadSettled,
  } = useDisplayedNote(
    personId,
    listingId,
    readOnly || hidesStoredNote,
    noteFromProps
  )
  const { savedNote: note, markSaved } = useLastSavedNote(displayedNote)
  const isWatchedStateUnknown =
    !readOnly &&
    watchOnNewNote &&
    wasWatchlisted === undefined &&
    !isWatchedStateError
  const { saveNote, clearNote, isBusy } = useNoteWrites(
    isReadSettled && !isWatchedStateUnknown
  )
  const newNoteWatches = watchOnNewNote && wasWatchlisted === false
  const { trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()

  const [mode, setMode] = useState<'view' | 'edit'>(defaultMode)
  const [draftNote, setDraftNote] = useState(note ?? '')
  const [isExpanded, setIsExpanded] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState<
    'idle' | 'saving' | 'saved'
  >('idle')
  const [hasSavedNote, setHasSavedNote] = useState(!!note)
  const [hasAutoSavedThisSession, setHasAutoSavedThisSession] = useState(false)
  const [debouncedDraft] = useDebounceValue(draftNote, 1500)
  const lastAutoSavedRef = useRef(note ?? '')
  const lastEditEventRef = useRef(note ?? '')
  const saveNoteRef = useRef(saveNote)
  saveNoteRef.current = saveNote // oxlint-disable-line react/refs
  const failedAutoSaveDraftRef = useRef<string | null>(null)
  const intlRef = useRef(intl)
  intlRef.current = intl // oxlint-disable-line react/refs
  const hasActiveNoteRef = useRef(!!note)
  const currentDraftRef = useRef(draftNote)
  currentDraftRef.current = draftNote // oxlint-disable-line react/refs
  const manualSaveActiveRef = useRef(false)
  const typedRef = useRef(false)
  const openedRef = useRef(false)

  const [isManualSaving, setIsManualSaving] = useState(false)

  useSeedFromListingNote(note, hasReadNote, {
    setDraftNote,
    setHasSavedNote,
    setMode,
    lastAutoSavedRef,
    lastEditEventRef,
    hasActiveNoteRef,
    currentDraftRef,
    typedRef,
    openedRef,
  })

  const isOverLimit = draftNote.length > MAX_NOTE_LENGTH
  const isLoading = isBusy
  const isWriteDisabled = isLoading || !personId
  const hasNoUnsavedChanges =
    hasAutoSavedThisSession &&
    // oxlint-disable-next-line react/refs
    draftNote.trim() === lastAutoSavedRef.current

  const clearActiveNote = useCallback(async () => {
    await clearNote({ personId, listingId })
    markSaved('')
    hasActiveNoteRef.current = false
    lastAutoSavedRef.current = ''
    lastEditEventRef.current = ''
    setHasSavedNote(false)

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [watchedStatusQuery.name] }),
      queryClient.invalidateQueries({
        queryKey: [allWatchedListingsQuery.name],
      }),
    ])

    showSnackbar({
      content: intl.formatMessage({ id: 'Listing.notes.removeSuccessToast' }),
      options: { type: 'default', autoClose: true, closeButton: true },
    })

    trackButtonClicked({ buttonName: 'delete_listing_note', screenName })
  }, [
    clearNote,
    markSaved,
    personId,
    listingId,
    queryClient,
    intl,
    trackButtonClicked,
    screenName,
  ])
  const clearActiveNoteRef = useRef(clearActiveNote)
  clearActiveNoteRef.current = clearActiveNote // oxlint-disable-line react/refs

  const handleRemoveNote = useCallback(async () => {
    manualSaveActiveRef.current = true
    setIsManualSaving(true)
    try {
      await clearActiveNote()
      setDraftNote('')
      setMode('view')
      onEditComplete?.()
    } catch {
      showSnackbar({
        content: intl.formatMessage({ id: 'Listing.notes.errorToast' }),
        options: { type: 'error', autoClose: true, closeButton: true },
      })
    } finally {
      manualSaveActiveRef.current = false
      setIsManualSaving(false)
    }
  }, [clearActiveNote, intl, onEditComplete])

  const handleEmptySave = useCallback(async () => {
    if (hasActiveNoteRef.current) {
      await handleRemoveNote()
      return
    }
    setDraftNote('')
    setMode('view')
    onEditComplete?.()
  }, [handleRemoveNote, onEditComplete])

  useEffect(() => {
    if (mode !== 'edit') return
    if (!personId || !accountId) return
    if (debouncedDraft !== currentDraftRef.current) return
    if (debouncedDraft.trim() === lastAutoSavedRef.current) return
    if (debouncedDraft.length > MAX_NOTE_LENGTH) return
    if (isBusy) return
    if (manualSaveActiveRef.current) return
    if (debouncedDraft === failedAutoSaveDraftRef.current) return

    const trimmed = debouncedDraft.trim()

    const autoClear = async () => {
      setAutoSaveStatus('saving')
      try {
        await clearActiveNoteRef.current()
        failedAutoSaveDraftRef.current = null
        setHasAutoSavedThisSession(true)
      } catch {
        failedAutoSaveDraftRef.current = debouncedDraft
      }
      setAutoSaveStatus('idle')
    }

    const autoSave = async () => {
      setAutoSaveStatus('saving')
      try {
        const isNewNote = !hasActiveNoteRef.current
        const outcome = await saveNoteRef.current({
          personId,
          listingId,
          accountId,
          dateExpires,
          listingEndTime,
          note: trimmed,
          watchesListing: newNoteWatches && isNewNote,
        })
        failedAutoSaveDraftRef.current = null
        lastAutoSavedRef.current = trimmed
        markSaved(trimmed)
        hasActiveNoteRef.current = true
        trackNoteSave({
          trackButtonClicked,
          screenName,
          note: trimmed,
          isNewNote,
          outcome,
          lastEditEventRef,
          minEditDelta: MIN_EDIT_CHAR_DELTA,
        })
        showNoteSaveToast(outcome, intlRef.current, true)
        if (outcome === 'watched') onAutoWatchlistAdd?.()
        setHasSavedNote(true)
        setHasAutoSavedThisSession(true)
        setAutoSaveStatus('saved')
        void queryClient.invalidateQueries({
          queryKey: [watchedStatusQuery.name],
        })
        void queryClient.invalidateQueries({
          queryKey: [allWatchedListingsQuery.name],
        })
      } catch {
        failedAutoSaveDraftRef.current = debouncedDraft
        setAutoSaveStatus('idle')
      }
    }

    if (trimmed) void autoSave()
    else if (hasActiveNoteRef.current) void autoClear()
  }, [
    debouncedDraft,
    mode,
    personId,
    accountId,
    dateExpires,
    listingEndTime,
    listingId,
    queryClient,
    isBusy,
    trackButtonClicked,
    screenName,
    newNoteWatches,
    onAutoWatchlistAdd,
    markSaved,
  ])

  useEffect(() => {
    if (autoSaveStatus !== 'saved') return
    const timer = setTimeout(() => setAutoSaveStatus('idle'), 3000)
    return () => clearTimeout(timer)
  }, [autoSaveStatus])

  const handleSave = useCallback(async () => {
    const trimmedDraft = draftNote.trim()
    if (!trimmedDraft) {
      await handleEmptySave()
      return
    }
    manualSaveActiveRef.current = true
    setIsManualSaving(true)
    const isNewNote = !hasActiveNoteRef.current
    try {
      const outcome = await saveNote({
        personId,
        listingId,
        accountId,
        dateExpires,
        listingEndTime,
        note: trimmedDraft,
        watchesListing: newNoteWatches && isNewNote,
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [watchedStatusQuery.name] }),
        queryClient.invalidateQueries({
          queryKey: [allWatchedListingsQuery.name],
        }),
      ])

      showNoteSaveToast(outcome, intl)
      hasActiveNoteRef.current = true
      trackNoteSave({
        trackButtonClicked,
        screenName,
        note: trimmedDraft,
        isNewNote,
        outcome,
        lastEditEventRef,
        minEditDelta: 0,
      })
      if (outcome === 'watched') onAutoWatchlistAdd?.()

      lastAutoSavedRef.current = trimmedDraft
      markSaved(trimmedDraft)
      setHasSavedNote(true)
      setMode('view')
      onEditComplete?.()
    } catch {
      showSnackbar({
        content: intl.formatMessage({ id: 'Listing.notes.errorToast' }),
        options: { type: 'error', autoClose: true, closeButton: true },
      })
    } finally {
      manualSaveActiveRef.current = false
      setIsManualSaving(false)
    }
  }, [
    handleEmptySave,
    saveNote,
    personId,
    listingId,
    accountId,
    dateExpires,
    listingEndTime,
    draftNote,
    newNoteWatches,
    queryClient,
    intl,
    onEditComplete,
    trackButtonClicked,
    screenName,
    onAutoWatchlistAdd,
    markSaved,
  ])

  const handleCancel = useCallback(() => {
    setDraftNote(note ?? '')
    lastAutoSavedRef.current = note ?? ''
    lastEditEventRef.current = note ?? ''
    hasActiveNoteRef.current = !!note
    setHasSavedNote(!!note)
    setHasAutoSavedThisSession(false)
    setMode('view')
    onEditComplete?.()
  }, [note, onEditComplete])

  const handleSwitchToEdit = useCallback(() => {
    openedRef.current = true
    setDraftNote(note ?? '')
    lastAutoSavedRef.current = note ?? ''
    lastEditEventRef.current = note ?? ''
    hasActiveNoteRef.current = !!note
    setHasSavedNote(!!note)
    setHasAutoSavedThisSession(false)
    setAutoSaveStatus('idle')
    setMode('edit')
  }, [note])

  const displayNote = note ?? ''
  const effectivePreviewLength = readOnly ? previewLength : NOTE_PREVIEW_LENGTH
  const isTruncated = displayNote.length > effectivePreviewLength
  const visibleNote =
    isTruncated && ((!readOnly && !isExpanded) || (readOnly && !!onExpand))
      ? `${displayNote.slice(0, effectivePreviewLength)}…`
      : displayNote

  return (
    <WidgetContainer>
      <WidgetHeader>
        <HeaderIcon icon={faFileLines} />
        <HeaderTitle>
          {intl.formatMessage({ id: 'Listing.notes.widgetTitle' })}
        </HeaderTitle>
        <PrivateBadge>
          {intl.formatMessage({ id: 'Listing.notes.privateLabel' })}
        </PrivateBadge>
      </WidgetHeader>

      <WidgetBody>
        {mode === 'edit' ? (
          <>
            <NoteTextarea
              value={draftNote}
              onChange={(e) => {
                typedRef.current = true
                setDraftNote(e.target.value.trimStart())
              }}
              placeholder={intl.formatMessage({
                id: 'Listing.notes.placeholder',
              })}
              maxLength={MAX_NOTE_LENGTH + 1} // +1 lets the 501st char through so the over-limit UI renders before the browser hard-blocks
              disabled={isManualSaving}
              aria-label={intl.formatMessage({
                id: 'Listing.notes.widgetTitle',
              })}
            />
            <CharacterCount
              $overLimit={isOverLimit}
              aria-live={isOverLimit ? 'assertive' : 'polite'}
              role={isOverLimit ? 'alert' : undefined}
            >
              {intl.formatMessage(
                { id: 'Listing.notes.characterCount' },
                { count: draftNote.length }
              )}
            </CharacterCount>
            <AutoSaveIndicator
              aria-live="polite"
              $success={autoSaveStatus === 'saved'}
            >
              {autoSaveStatus === 'saving' &&
                intl.formatMessage({ id: 'Listing.notes.autoSaving' })}
              {autoSaveStatus === 'saved' &&
                intl.formatMessage({ id: 'Listing.notes.autoSaved' })}
            </AutoSaveIndicator>
            <EditActions>
              <ActionButton
                $variant="primary"
                onClick={handleSave}
                disabled={isWriteDisabled || isOverLimit || hasNoUnsavedChanges}
              >
                {intl.formatMessage({ id: 'Listing.notes.save' })}
              </ActionButton>
              <ActionButton
                $variant="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {intl.formatMessage({ id: 'Listing.notes.cancel' })}
              </ActionButton>
              {hasSavedNote && (
                <ActionButton
                  $variant="danger"
                  onClick={handleRemoveNote}
                  disabled={isWriteDisabled}
                >
                  {intl.formatMessage({ id: 'Listing.notes.removeNote' })}
                </ActionButton>
              )}
            </EditActions>
          </>
        ) : (
          <>
            {displayNote ? (
              <>
                <NoteText
                  onClick={!readOnly ? handleSwitchToEdit : undefined}
                  style={!readOnly ? { cursor: 'pointer' } : undefined}
                >
                  {visibleNote}
                </NoteText>
                {isTruncated &&
                  (!readOnly ? (
                    <ShowMoreButton
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsExpanded((prev) => !prev)
                      }}
                    >
                      {isExpanded
                        ? intl.formatMessage({ id: 'Listing.notes.showLess' })
                        : intl.formatMessage({ id: 'Listing.notes.showMore' })}
                    </ShowMoreButton>
                  ) : onExpand ? (
                    <ShowMoreButton
                      onClick={(e) => {
                        e.stopPropagation()
                        onExpand()
                      }}
                    >
                      {intl.formatMessage({ id: 'Listing.notes.showMore' })}
                    </ShowMoreButton>
                  ) : null)}
              </>
            ) : (
              <PlaceholderText
                onClick={!readOnly ? handleSwitchToEdit : undefined}
                style={!readOnly ? { cursor: 'pointer' } : undefined}
              >
                {intl.formatMessage({ id: 'Listing.notes.placeholder' })}
              </PlaceholderText>
            )}
          </>
        )}
      </WidgetBody>
    </WidgetContainer>
  )
}

export default NotesWidget
